/**
 * InsightPro™ — Notes & Commentary (Multi-Entity Finance only)
 *
 * Per (period, brand) commentary stored in Firestore:
 *   companies/{companyId}/notes/{periodKey}__{brandSlug}
 *     { body, period, brand, updatedBy: { uid, email }, updatedAt }
 *
 * Lightweight markdown supported in the editor (bold, italic, headings, lists).
 * Recent notes list lets the CFO jump back to prior commentary; current edit
 * shows preview side-by-side. Save is explicit (no auto-save).
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  getFirestore, doc, setDoc, getDoc, collection, query, orderBy, limit, onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { auth } from '../utils/firebase';
import { useCompany } from '../context/CompanyContext';
import { fetchFinancePeriods, fetchFinanceBrands } from '../utils/api';

const db = getFirestore();

const brandSlug = (b) => (b || 'all').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'all';
const noteId    = (period, brand) => `${period}__${brandSlug(brand)}`;

// Tiny markdown renderer — bold/italic/H2/H3/UL/paragraph. Escapes HTML first.
function renderMarkdown(src) {
  if (!src) return '';
  let s = src
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Headings
  s = s.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  s = s.replace(/^# (.+)$/gm, '<h2>$1</h2>');
  // Bold + italic (bold first to avoid * inside **)
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Lists
  s = s.replace(/^- (.+)$/gm, '<li>$1</li>');
  s = s.replace(/(<li>.*?<\/li>(?:\n<li>.*?<\/li>)*)/gs, '<ul>$1</ul>');
  // Paragraphs (double newlines)
  s = s.split(/\n{2,}/).map(block => {
    if (/^<(h2|h3|ul)/.test(block)) return block;
    return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
  }).join('\n');
  return s;
}

const fmtDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const S = {
  page:        { padding: '20px 28px', maxWidth: 1500 },
  header:      { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  title:       { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  body:        { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px', marginBottom: 16 },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12 },
  toolbar:     { display: 'flex', gap: 6, marginBottom: 10 },
  tab:         { padding: '5px 12px', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', border: '1px solid var(--card-border)', borderRadius: 2, background: 'transparent', color: 'var(--page-muted)' },
  tabActive:   { background: 'var(--gold)', borderColor: 'var(--gold)', color: 'var(--void)', fontWeight: 700 },
  textarea:    { width: '100%', minHeight: 380, padding: 12, fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.55, color: 'var(--page-text)', background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 2, outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  preview:     { padding: '14px 18px', minHeight: 380, fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.65, color: 'var(--page-text)', background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 2, overflowY: 'auto' },
  saveBar:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12 },
  saveBtn:     { padding: '8px 18px', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--gold)', color: 'var(--void)', border: 'none', borderRadius: 2, cursor: 'pointer', fontWeight: 700 },
  saveBtnDis:  { background: 'var(--card-border)', color: 'var(--page-muted)', cursor: 'not-allowed' },
  meta:        { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)' },
  recentItem:  { padding: '10px 12px', borderBottom: '1px solid var(--card-border)', cursor: 'pointer', transition: 'background 0.1s' },
  recentHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  recentBadge: { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--amber)', background: 'rgba(200,149,32,0.08)', padding: '2px 6px', borderRadius: 2, textTransform: 'uppercase' },
  recentDate:  { fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)' },
  recentSnip:  { fontSize: 12, color: 'var(--page-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' },
  recentBy:    { fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', marginTop: 4 },
  empty:       { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  successPill: { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--green-l)' },
};

const PLACEHOLDER = `# March 2026 Commentary

Group sales of R152.27M, up 12% on prior month, driven by Pedros DCs.

## Highlights
- Operating margin held at 5.3% despite mix shift to lower-margin DC business
- Cash position improved to R28M (+R4M on Feb)

## Watch points
- KZN province down 8% vs prior month — investigating
- Working capital tied up in inventory at year-end stocktake

*Use **bold**, *italic*, headings (#, ##) and bullet lists (-) for formatting.*`;

export default function Notes() {
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || selectedCompany?.companyId;

  const [periods, setPeriods]   = useState([]);
  const [period,  setPeriod]    = useState(null);
  const [brands,  setBrands]    = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]     = useState('Consolidated');

  const [body,         setBody]         = useState('');
  const [originalBody, setOriginalBody] = useState('');
  const [view,         setView]         = useState('split'); // edit | preview | split
  const [saving,       setSaving]       = useState(false);
  const [savedAt,      setSavedAt]      = useState(null);
  const [updatedBy,    setUpdatedBy]    = useState(null);
  const [err,          setErr]          = useState(null);
  const [recent,       setRecent]       = useState([]);

  // Load periods + brands once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        setBrand(b.canPickBrand ? 'Consolidated' : (b.brands[0] || 'Consolidated'));
      } catch (e) { if (!cancelled) setErr(e.response?.data?.error || e.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Subscribe to recent notes (latest 12 across all period+brand combos)
  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, 'companies', companyId, 'notes'),
      orderBy('updatedAt', 'desc'),
      limit(12),
    );
    const unsub = onSnapshot(q, snap => {
      setRecent(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, e => setErr(e.message));
    return () => unsub();
  }, [companyId]);

  // Load current note when period/brand changes
  useEffect(() => {
    if (!companyId || !period) return;
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'companies', companyId, 'notes', noteId(period, brand));
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data();
          setBody(data.body || '');
          setOriginalBody(data.body || '');
          setSavedAt(data.updatedAt || null);
          setUpdatedBy(data.updatedBy || null);
        } else {
          setBody('');
          setOriginalBody('');
          setSavedAt(null);
          setUpdatedBy(null);
        }
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [companyId, period, brand]);

  const dirty = body !== originalBody;

  const save = async () => {
    if (!companyId || !period || !dirty) return;
    setSaving(true); setErr(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not signed in');
      await setDoc(
        doc(db, 'companies', companyId, 'notes', noteId(period, brand)),
        {
          body,
          period,
          brand,
          updatedBy: { uid: user.uid, email: user.email || '' },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setOriginalBody(body);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Notes & Commentary</div>
          <div style={S.sub}>
            {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} only`}
            {' · '}{period || '—'}
          </div>
        </div>
        <div style={S.selectorBar}>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}

      <div style={S.body}>
        {/* Editor + preview */}
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>
              Commentary — {period} {brand !== 'Consolidated' && ` · ${brand}`}
            </div>
            <div style={S.toolbar}>
              {['edit', 'split', 'preview'].map(v => (
                <button key={v} style={{ ...S.tab, ...(view === v ? S.tabActive : {}) }} onClick={() => setView(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: view === 'split' ? '1fr 1fr' : '1fr',
              gap: 12,
            }}>
              {(view === 'edit' || view === 'split') && (
                <textarea
                  style={S.textarea}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder={PLACEHOLDER}
                />
              )}
              {(view === 'preview' || view === 'split') && (
                <div
                  style={S.preview}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(body) || '<p style="color:var(--page-muted);font-style:italic">Nothing to preview yet.</p>' }}
                />
              )}
            </div>
            <div style={S.saveBar}>
              <div style={S.meta}>
                {savedAt
                  ? `Saved ${fmtDate(savedAt)}${updatedBy?.email ? ' · by ' + updatedBy.email : ''}`
                  : 'No saves yet for this period+brand'}
                {dirty && <span style={{ color: 'var(--amber)', marginLeft: 8 }}>· Unsaved changes</span>}
                {!dirty && savedAt && <span style={S.successPill}> · Up to date</span>}
              </div>
              <button
                style={{ ...S.saveBtn, ...((!dirty || saving) ? S.saveBtnDis : {}) }}
                onClick={save}
                disabled={!dirty || saving}
              >{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>

        {/* Recent notes sidebar */}
        <div style={S.card}>
          <div style={S.cardTitle}>Recent Notes</div>
          {recent.length === 0 ? (
            <div style={S.empty}>No commentary yet. Start writing for {period}.</div>
          ) : recent.map(n => {
            const snippet = (n.body || '').replace(/[#*-]/g, '').replace(/\n+/g, ' ').slice(0, 80);
            const isCurrent = n.id === noteId(period, brand);
            return (
              <div
                key={n.id}
                style={{
                  ...S.recentItem,
                  background: isCurrent ? 'rgba(200,149,32,0.06)' : 'transparent',
                }}
                onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                onClick={() => {
                  setPeriod(n.period);
                  setBrand(n.brand);
                }}
              >
                <div style={S.recentHead}>
                  <span style={S.recentBadge}>{n.period} · {n.brand}</span>
                  <span style={S.recentDate}>{fmtDate(n.updatedAt)}</span>
                </div>
                <div style={S.recentSnip}>{snippet}{(n.body || '').length > 80 ? '…' : ''}</div>
                {n.updatedBy?.email && <div style={S.recentBy}>{n.updatedBy.email}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
